import { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import {
    getScopeKeysFromSerialized,
    serializeContentEditable,
    hydrateContentEditable,
} from '../scopeUtils';

/**
 * Composer state and behaviour: input, scope picker, PII resolution.
 */
export function useChatComposer({ isOpen, schoolClasses, schoolStudents }) {
    const [inputValue, setInputValue] = useState('');
    const [scopePickerOpen, setScopePickerOpen] = useState(false);
    const [pendingPiiResolution, setPendingPiiResolution] = useState(null);
    const [piiSearchQueries, setPiiSearchQueries] = useState({});

    const inputRef = useRef(null);
    const savedSlashRangeRef = useRef(null);
    const lastKnownRangeRef = useRef(null);

    const existingScopeKeys = useMemo(
        () => getScopeKeysFromSerialized(inputValue),
        [inputValue]
    );

    useEffect(() => {
        const el = inputRef.current;
        if (!el) return;
        const onSelectionChange = () => {
            const sel = window.getSelection();
            const range = sel?.rangeCount ? sel.getRangeAt(0) : null;
            if (range && el.contains(range.commonAncestorContainer)) {
                try {
                    lastKnownRangeRef.current = range.cloneRange();
                } catch (_) {
                    /* cloneRange can throw if range is detached; ignore */
                }
            }
        };
        document.addEventListener('selectionchange', onSelectionChange);
        return () => document.removeEventListener('selectionchange', onSelectionChange);
    }, [isOpen]);

    useEffect(() => {
        if (!isOpen) {
            setPendingPiiResolution(null);
            setPiiSearchQueries({});
            setInputValue('');
            if (inputRef.current) {
                inputRef.current.textContent = '';
            }
        }
    }, [isOpen]);

    useEffect(() => {
        if (scopePickerOpen && inputRef.current) {
            handleInputSync();
        }
    }, [scopePickerOpen]);

    useEffect(() => {
        if (pendingPiiResolution && inputRef.current) {
            inputRef.current.textContent = pendingPiiResolution.sanitizedMessage;
        }
    }, [pendingPiiResolution?.sanitizedMessage]);

    const handleInputSync = useCallback(() => {
        const raw = serializeContentEditable(inputRef.current);
        setInputValue(raw);
    }, []);

    const handleKeyDown = useCallback((e, onSendMessage) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            onSendMessage?.();
            return;
        }
        if (e.key === '@') {
            e.preventDefault();
            const selection = window.getSelection();
            const range = selection?.rangeCount ? selection.getRangeAt(0) : null;
            if (range && inputRef.current?.contains(range.commonAncestorContainer)) {
                savedSlashRangeRef.current = range.cloneRange();
            } else {
                savedSlashRangeRef.current = null;
            }
            setScopePickerOpen(true);
        }
    }, []);

    const handleScopeDismiss = useCallback(() => {
        const el = inputRef.current;
        const savedRange = savedSlashRangeRef.current;
        savedSlashRangeRef.current = null;
        setScopePickerOpen(false);
        el?.focus();
        if (el && savedRange && el.contains(savedRange.startContainer)) {
            try {
                const text = document.createTextNode('@');
                savedRange.insertNode(text);
                savedRange.setStartAfter(text);
                savedRange.collapse(true);
                const sel = window.getSelection();
                if (sel) {
                    sel.removeAllRanges();
                    sel.addRange(savedRange);
                }
            } catch (err) {
                el.appendChild(document.createTextNode('@'));
            }
            handleInputSync();
        }
    }, [handleInputSync]);

    const handleScopeSelect = useCallback(
        (entity) => {
            const el = inputRef.current;
            if (!el) return;
            const key = `${entity.type}:${entity.id}`;
            const currentKeys = getScopeKeysFromSerialized(serializeContentEditable(el));
            if (currentKeys.has(key)) return;
            const selection = window.getSelection();
            const currentRange = selection?.rangeCount ? selection.getRangeAt(0) : null;
            const currentInEl = currentRange && el.contains(currentRange.commonAncestorContainer);
            const savedFromAt = savedSlashRangeRef.current;
            const savedFromAtValid = savedFromAt && el.contains(savedFromAt.startContainer);
            const lastKnown = lastKnownRangeRef.current;
            const lastKnownValid = lastKnown && el.contains(lastKnown.startContainer);
            const insertRange = savedFromAtValid
                ? savedFromAt
                : lastKnownValid
                  ? lastKnown
                  : currentInEl
                    ? currentRange
                    : null;

            const span = document.createElement('span');
            span.contentEditable = 'false';
            span.setAttribute('data-entity-type', entity.type);
            span.setAttribute('data-entity-id', entity.id);
            span.className = 'ai-chat-inline-scope';
            span.textContent = entity.label;
            const spaceBefore = document.createTextNode('\u00A0');
            const spaceAfter = document.createTextNode('\u00A0');

            if (insertRange) {
                insertRange.collapse(true);
                insertRange.insertNode(spaceAfter);
                insertRange.collapse(true);
                insertRange.insertNode(span);
                insertRange.collapse(true);
                insertRange.insertNode(spaceBefore);
                insertRange.setStartAfter(spaceAfter);
                insertRange.collapse(true);
                if (selection) {
                    selection.removeAllRanges();
                    selection.addRange(insertRange);
                }
                lastKnownRangeRef.current = insertRange.cloneRange();
            } else {
                el.appendChild(spaceBefore);
                el.appendChild(span);
                el.appendChild(spaceAfter);
                const endRange = document.createRange();
                endRange.setStartAfter(spaceAfter);
                endRange.collapse(true);
                if (selection) {
                    selection.removeAllRanges();
                    selection.addRange(endRange);
                }
                lastKnownRangeRef.current = endRange.cloneRange();
            }
            savedSlashRangeRef.current = null;
            el.focus();
            handleInputSync();
        },
        [handleInputSync]
    );

    const handlePiiReplace = useCallback(
        (placeholderKey, scope) => {
            const nextStr = inputValue.replace(placeholderKey, `{{${scope.type}:${scope.id}}}`);
            setInputValue(nextStr);
            if (inputRef.current) {
                hydrateContentEditable(inputRef.current, nextStr, schoolClasses, schoolStudents);
            }
        },
        [inputValue, schoolClasses, schoolStudents]
    );

    const handlePiiDismiss = useCallback(() => {
        setPendingPiiResolution(null);
        setPiiSearchQueries({});
    }, []);

    return {
        inputRef,
        inputValue,
        setInputValue,
        handleInputSync,
        handleKeyDown,
        handleScopeSelect,
        handleScopeDismiss,
        handlePiiReplace,
        handlePiiDismiss,
        scopePickerOpen,
        setScopePickerOpen,
        pendingPiiResolution,
        setPendingPiiResolution,
        piiSearchQueries,
        setPiiSearchQueries,
        existingScopeKeys,
    };
}
